import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'moonlight';
type Direction = 'ltr' | 'rtl';

interface ThemeContextType {
  theme: Theme;
  direction: Direction;
  toggleTheme: () => void;
  toggleDirection: () => void;
  setTheme: (theme: Theme) => void;
  setDirection: (direction: Direction) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');
  const [direction, setDirectionState] = useState<Direction>('rtl');

  useEffect(() => {
    // Load theme and direction from localStorage
    const savedTheme = localStorage.getItem('theme') as Theme;
    const savedDirection = localStorage.getItem('direction') as Direction;
    
    if (savedTheme) {
      setThemeState(savedTheme);
    }
    
    if (savedDirection) {
      setDirectionState(savedDirection);
    }
  }, []);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('dir', direction);
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
    localStorage.setItem('direction', direction);
  }, [theme, direction]);

  const toggleTheme = () => {
    setThemeState(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'moonlight';
      return 'light';
    });
  };

  const toggleDirection = () => {
    setDirectionState(prev => prev === 'ltr' ? 'rtl' : 'ltr');
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const setDirection = (newDirection: Direction) => {
    setDirectionState(newDirection);
  };

  const value = {
    theme,
    direction,
    toggleTheme,
    toggleDirection,
    setTheme,
    setDirection,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 